// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { OperatorConfiguration } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Configuration/Operator';
import FormDialog from '../../../components/Dialog/FormDialog';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useCreateSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import {
  CredentialRequirement,
  credentialRequirementsToTuples,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import { ERR_CRE_OP_CONF, SUC_CRE_OP_CONF } from '../../../utils/strings';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateOperatorConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [providerCredentialRequirements, setProviderCredentialRequirements] = useState<
    CredentialRequirement[]
  >([]);
  const party = useModuleParty();

  const { submit: createOperatorConfiguration } = useCreateSubmission(
    OperatorConfiguration,
    SUC_CRE_OP_CONF,
    ERR_CRE_OP_CONF,
  );

  const resetState = useCallback(() => {
    setProviderCredentialRequirements([]);
  }, [setProviderCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    await createOperatorConfiguration({
      argument: {
        operator: party,
        userRequirements: credentialRequirementsToTuples(providerCredentialRequirements),
      },
    });
    handleClose();
  }, [party, providerCredentialRequirements, createOperatorConfiguration, handleClose]);

  return (
    <FormDialog
      open={open}
      title="Create Operator Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={false}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={providerCredentialRequirements}
          setList={setProviderCredentialRequirements}
          title="Required Credentials for Users"
        />
      </Box>
    </FormDialog>
  );
};
